import React, { useState } from 'react';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import { useAuth } from '../AuthContext';
import api from '../api';
import tokens from '../theme/tokens';

/**
 * Liberação de convidados na pelada aberta (apenas Admin/Diretor), com confirmação.
 * `pelada` precisa ter _id e openGuest; `onChange(peladaAtualizada)` recebe o retorno da API.
 */
const GuestToggle = ({ pelada, onChange, onMessage, sx }) => {
  const { userLogado } = useAuth();
  const [confirmar, setConfirmar] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const podeGerenciar = userLogado && (userLogado.perfil === 'Admin' || userLogado.perfil === 'Diretor');

  if (!podeGerenciar || !pelada || !pelada._id) return null;
  const liberado = Boolean(pelada.openGuest);

  const aplicar = async () => {
    setSalvando(true);
    try {
      const response = await api.post('openGuest', { peladaId: pelada._id, aberto: confirmar });
      onChange && onChange(response.data.data);
      onMessage && onMessage(confirmar ? 'Convidados liberados para a pelada.' : 'Convidados bloqueados para a pelada.');
    } catch (error) {
      onMessage && onMessage(error.response?.data?.error || 'Não foi possível alterar a liberação de convidados.', 'error');
    }
    setSalvando(false);
    setConfirmar(null);
  };

  return (
    <>
      <Card sx={{ p: 2, borderColor: liberado ? tokens.color.success : undefined, bgcolor: liberado ? tokens.color.successSoft : undefined, ...sx }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: liberado ? tokens.color.success : tokens.color.surfaceAlt, color: liberado ? '#fff' : 'text.secondary', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <PersonAddAlt1RoundedIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography fontWeight={700}>Convidados na pelada</Typography>
            <Typography variant="body2" color="text.secondary">
              {liberado ? 'Liberado: jogadores confirmados podem adicionar convidados.' : 'Bloqueado: ninguém pode adicionar convidados.'}
            </Typography>
          </Box>
          <Switch checked={liberado} onChange={(e) => setConfirmar(e.target.checked)} disabled={salvando} inputProps={{ 'aria-label': 'Liberar convidados' }} />
        </Stack>
      </Card>

      <Dialog open={confirmar !== null} onClose={() => setConfirmar(null)}>
        <DialogTitle>{confirmar ? 'Liberar convidados?' : 'Bloquear convidados?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmar
              ? 'Os jogadores confirmados poderão adicionar convidados na pelada aberta. A diária de cada convidado é cobrada do anfitrião, e todos recebem uma notificação avisando da liberação.'
              : 'Novos convidados não poderão mais ser adicionados. Os convidados já incluídos continuam na lista.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmar(null)} color="inherit">Cancelar</Button>
          <Button onClick={aplicar} variant="contained" color={confirmar ? 'primary' : 'error'} disabled={salvando}>
            {confirmar ? 'Liberar convidados' : 'Bloquear convidados'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GuestToggle;
